import { CSSProperties, FC } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton, SkeletonFade } from '@/shared/components/skeletons';

import { ColumnarListGridOptions, getColumnarListGridStyle } from '../ColumnarListLayout';

const HEADER_WIDTHS = ['58%', '46%', '52%', '38%'];
const ROW_WIDTHS = ['68%', '54%', '61%', '45%'];

interface ColumnarListSkeletonProps extends ColumnarListGridOptions {
    ariaLabel: string;
    avatarShape?: 'circle' | 'square';
    avatarSize?: number;
    includeHeader?: boolean;
    rowDataAttribute?: string;
    rows: number;
    style?: CSSProperties;
}

export interface ColumnarListSkeletonRowProps extends ColumnarListGridOptions {
    avatarShape?: 'circle' | 'square';
    avatarSize?: number;
    rowDataAttribute?: string;
    rowIndex?: number;
    style?: CSSProperties;
}

const SkeletonBar: FC<{ height?: string; width: string }> = ({ height = '0.75rem', width }) => (
    <ButtonSkeleton block size='small' style={{ display: 'block', height, maxWidth: '100%', minWidth: 0, width }} />
);

const IdentitySkeleton: FC<{ avatarShape: 'circle' | 'square'; avatarSize: number; rowIndex: number }> = ({
    avatarShape,
    avatarSize,
    rowIndex,
}) => (
    <div
        style={{
            alignItems: 'center',
            display: 'grid',
            gap: '0.625rem',
            gridTemplateColumns: `1rem ${avatarSize}px minmax(0, 1fr)`,
            minWidth: 0,
        }}
    >
        <ButtonSkeleton shape='square' size='small' style={{ height: '1rem', minWidth: '1rem', width: '1rem' }} />
        <AvatarSkeleton shape={avatarShape} size={avatarSize} />
        <div style={{ minWidth: 0 }}>
            <SkeletonBar height='0.8rem' width={rowIndex % 3 === 1 ? '64%' : '76%'} />
            <div style={{ marginTop: '0.375rem' }}>
                <SkeletonBar height='0.625rem' width={rowIndex % 2 === 0 ? '42%' : '52%'} />
            </div>
        </div>
    </div>
);

export const ColumnarListSkeletonRow: FC<ColumnarListSkeletonRowProps> = ({
    actionColumnWidthRem,
    avatarShape = 'square',
    avatarSize = 40,
    columnCount,
    columnGapRem,
    columnMinWidthRem,
    identityMinWidthRem,
    minWidthBufferRem,
    rowDataAttribute,
    rowIndex = 0,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const gridStyle = getColumnarListGridStyle({
        actionColumnWidthRem,
        columnCount,
        columnGapRem,
        columnMinWidthRem,
        identityMinWidthRem,
        minWidthBufferRem,
    });

    return (
        <div
            data-columnar-list-skeleton-row='true'
            {...(rowDataAttribute ? { [rowDataAttribute]: 'true' } : {})}
            style={{
                ...gridStyle,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                boxSizing: 'border-box',
                minHeight: '4rem',
                padding: '0.5rem',
                ...style,
            }}
        >
            <IdentitySkeleton avatarShape={avatarShape} avatarSize={avatarSize} rowIndex={rowIndex} />
            {Array.from({ length: columnCount }, (_column, columnIndex) => (
                <div key={columnIndex} style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                    <SkeletonBar width={ROW_WIDTHS[(rowIndex + columnIndex) % ROW_WIDTHS.length]} />
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ButtonSkeleton
                    shape='square'
                    size='small'
                    style={{ height: '1.5rem', minWidth: '1.5rem', width: '1.5rem' }}
                />
            </div>
        </div>
    );
};

const ColumnarListSkeleton: FC<ColumnarListSkeletonProps> = ({
    actionColumnWidthRem,
    ariaLabel,
    avatarShape = 'square',
    avatarSize = 40,
    columnCount,
    columnGapRem,
    columnMinWidthRem,
    identityMinWidthRem,
    includeHeader = true,
    minWidthBufferRem,
    rowDataAttribute,
    rows,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const gridStyle = getColumnarListGridStyle({
        actionColumnWidthRem,
        columnCount,
        columnGapRem,
        columnMinWidthRem,
        identityMinWidthRem,
        minWidthBufferRem,
    });

    return (
        <SkeletonFade style={{ height: '100%', overflow: 'hidden', width: '100%' }}>
            <div
                aria-label={ariaLabel}
                role='status'
                style={{
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    padding: '0.5rem 0.75rem 0.75rem',
                    width: '100%',
                    ...style,
                }}
            >
                <div style={{ overflow: 'hidden', width: '100%' }}>
                    {includeHeader ? (
                        <div
                            style={{
                                ...gridStyle,
                                backgroundColor: token.colorBgBase,
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadius,
                                boxSizing: 'border-box',
                                minHeight: '2.5rem',
                                padding: '0.25rem 0.5rem',
                            }}
                        >
                            <div
                                style={{
                                    alignItems: 'center',
                                    display: 'grid',
                                    gap: '0.625rem',
                                    gridTemplateColumns: '1rem minmax(0, 1fr)',
                                    minWidth: 0,
                                }}
                            >
                                <ButtonSkeleton
                                    shape='square'
                                    size='small'
                                    style={{ height: '1rem', minWidth: '1rem', width: '1rem' }}
                                />
                                <SkeletonBar height='0.7rem' width='7rem' />
                            </div>
                            {Array.from({ length: columnCount }, (_value, index) => (
                                <div key={index} style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                                    <SkeletonBar width={HEADER_WIDTHS[index % HEADER_WIDTHS.length]} />
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <ButtonSkeleton
                                    shape='square'
                                    size='small'
                                    style={{ height: '1.25rem', minWidth: '1.25rem', width: '1.25rem' }}
                                />
                            </div>
                        </div>
                    ) : null}
                    <div style={{ marginTop: includeHeader ? '0.25rem' : 0 }}>
                        {Array.from({ length: rows }, (_value, rowIndex) => (
                            <ColumnarListSkeletonRow
                                actionColumnWidthRem={actionColumnWidthRem}
                                avatarShape={avatarShape}
                                avatarSize={avatarSize}
                                columnCount={columnCount}
                                columnGapRem={columnGapRem}
                                columnMinWidthRem={columnMinWidthRem}
                                identityMinWidthRem={identityMinWidthRem}
                                key={rowIndex}
                                minWidthBufferRem={minWidthBufferRem}
                                rowDataAttribute={rowDataAttribute}
                                rowIndex={rowIndex}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </SkeletonFade>
    );
};

export default ColumnarListSkeleton;
